import {
  ComboBox,
  DefaultButton,
  DetailsListLayoutMode,
  IColumn,
  IComboBox,
  IComboBoxOption,
  IObjectWithKey,
  ISelectionZoneProps,
  IStackStyles,
  Selection,
  SelectionMode,
  Stack,
  TooltipHost,
  TooltipOverflowMode,
} from "@fluentui/react";
import { CredentialPanel } from "components/management/credentials/Panel";
import {
  useDeleteCredential,
  useEditorPanelState,
} from "components/management/credentials/hooks";
import { PanelBodyCreate } from "components/management/credentials";
import { useRef, useState } from "react";
import { useQueryClient } from "react-query";
import { ICredentialInfo } from "types/stac";
import {
  QUERY_KEYS,
  useCredentialTypes,
  useManagedIdentities,
} from "utils/requests";
import { PanelMode } from "components/management/types";
import { CredentialsCommandBar } from "components/management/credentials/CommandBar";
import { useUpsertCredential } from "components/management/credentials/hooks";
import { SortableDetailsList } from "components/management/common/SortedDetailList";
import { useResizableColumns } from "components/management/common/useResizableColumns";
import { NoRecords } from "components/NoRecords";
import { useLocation } from "react-router-dom";

interface CredentialsTabProps {
  credentials: ICredentialInfo[];
  isLoading: boolean;
  onNextPage: () => void;
  onPreviousPage: () => void;
  onPageChange: (size: number) => void;
}

export const CredentialsTab = ({
  credentials,
  isLoading,
  onNextPage: onNext,
  onPreviousPage: onPrevious,
  onPageChange: onPageSizeChange,
}: CredentialsTabProps) => {
  const queryClient = useQueryClient();
  // define a default object to be used as the initial value for the panel
  const [detailItem, setDetailItem] = useState<ICredentialInfo>();
  const [panelMode, setPanelMode] = useState<PanelMode>("create");

  // generate fake data with the value SasToken, SAS Token
  const { data: credentialsTypes } = useCredentialTypes();
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(10);
  const { data: managedIdentities } = useManagedIdentities();

  const {
    showDeleteDialog,
    setShowDeleteDialog,
    deleteDialog,
    isError,
    errorDialog,
  } = useDeleteCredential(detailItem?.id || "", currentPage, pageSize);

  const handleCommandBarButtonClick = (mode: PanelMode) => {
    getSelectedCredential();
    setPanelMode(mode);
    handleCreateOpen();
  };

  const selectionRef = useRef(
    new Selection({
      getKey(item: IObjectWithKey) {
        const key = (item as ICredentialInfo).id || "default";
        return key;
      },
      onSelectionChanged: () => {
        getSelectedCredential();
      },
    })
  );

  const getSelectedCredential = () => {
    const selectedItems = selectionRef.current.getSelection();
    const item = selectedItems.length ? selectedItems[0] : null;
    setDetailItem(item as ICredentialInfo);
  };

  const location = useLocation();
  const { panelState, handleCreateOpen, handlePanelClose } = useEditorPanelState({
    isOpen: (location.state && location.state.isOpen) || false,
  });

  const mutation = useUpsertCredential(
    detailItem?.id || "",
    currentPage,
    pageSize,
    panelMode
  );

  const handleRefresh = () => {
    credentials.forEach(credential => {
      queryClient.invalidateQueries([QUERY_KEYS.credentialsDetail, credential.id]);
    });
    queryClient.invalidateQueries([QUERY_KEYS.credentials, currentPage, pageSize]);
  };

  const onRenderItemValue = (fieldContent: string) => {
    return (
      <TooltipHost content={fieldContent} overflowMode={TooltipOverflowMode.Parent}>
        <span>{fieldContent}</span>
      </TooltipHost>
    );
  };
  const containerStyles: IStackStyles = {
    root: {
      height: "calc(100vh - 250px)", // Make the Stack fill the viewport height
      minHeight: "150px", // Ensure a minimum height
      display: "flex",
      flexDirection: "column",
    },
  };

  const contentStyles: IStackStyles = {
    root: {
      flexGrow: 1, // Allow the content area to grow and fill the space
      overflow: "auto", // Add scroll to the content area if needed
    },
  };

  const footerStyles: IStackStyles = {
    root: {
      horizontalAlign: "center",
    },
  };
  const buttonStyles = { root: { marginRight: 8 } };

  const baseColumns: IColumn[] = [
    {
      key: "id",
      name: "Ingestion source ID",
      fieldName: "id",
      minWidth: 250,
      isResizable: true,
    },
    {
      key: "key",
      name: "Type",
      fieldName: "sourceType",
      minWidth: 80,
      isResizable: true,
    },
    {
      key: "created",
      name: "Creation Date",
      fieldName: "created",
      minWidth: 100,
      isSorted: true,
      isSortedDescending: true,
      isResizable: true,
      isCollapsible: true,
      onRender: (item: ICredentialInfo) => formatDateTime(item.created),
    },
    {
      key: "connectionInfo.containerUrl",
      name: "Container URL",
      fieldName: "connectionInfo.containerUrl",
      minWidth: 350,
      isResizable: true,
      isCollapsible: false,
      onRender: (item: ICredentialInfo) =>
        onRenderItemValue(item.connectionInfo?.containerUrl || ""),
    },
    {
      key: "connectionInfo.expiration",
      name: "Expiration",
      fieldName: "connectionInfo.expiration",
      minWidth: 100,
      isResizable: true,
      isCollapsible: false,
      onRender: (item: ICredentialInfo) =>
        formatDateTime(item.connectionInfo?.expiration || ""),
    },
  ];

  const {
    columns,
    detailsListRef,
    onColumnHeaderContextMenu,
    onColumnResize,
    headerContextMenu,
  } = useResizableColumns(baseColumns);

  const formatDateTime = (dateString: string | undefined): string => {
    if (dateString === undefined || dateString === "") {
      return "Unknown";
    }
    const date = new Date(dateString);
    const formattedDate = `${
      date.getMonth() + 1
    }/${date.getDate()}/${date.getFullYear()}`;
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const formattedTime = `${hours.toString().padStart(2, "0")}:${minutes
      .toString()
      .padStart(2, "0")}`;
    return `${formattedDate} ${formattedTime}`;
  };

  const getSelectionZone = (selection: Selection): ISelectionZoneProps => {
    return {
      selection: selection,
      selectionMode: SelectionMode.single,
      selectionPreservedOnEmptyClick: true,
    };
  };

  // todo
  // const items = data?.pages.map(page => page.features).flat() || [];
  const credentialsCollection = credentials;

  const table = (
    <SortableDetailsList
      className="collection-items-table"
      componentRef={detailsListRef}
      layoutMode={DetailsListLayoutMode.fixedColumns}
      items={credentialsCollection}
      columns={columns}
      selection={selectionRef.current}
      selectionMode={SelectionMode.single}
      selectionZoneProps={getSelectionZone(selectionRef.current)}
      getKey={item => item.id}
      setKey="items"
      enableSorting={!isLoading}
      initialSortColumn={"created"}
      onColumnHeaderContextMenu={onColumnHeaderContextMenu}
      onColumnResize={onColumnResize}
      onRenderRow={(props, defaultRender) => {
        if (!props || !defaultRender) return null;
        const credential = props.item as ICredentialInfo;
        return defaultRender({
          ...props,
          checkButtonAriaLabel: `Select credential ${
            credential.id ?? credential.connectionInfo?.containerUrl
          }`,
        });
      }}
    />
  );
  const handlePrevious = () => {
    setCurrentPage(prevPage => Math.max(0, prevPage - 1));
    onPrevious();
  };

  // We need a method handleCurrentPageChange to handle the change of the current page
  const handleCurrentPageChange = (size: number) => {
    onPageSizeChange(size);
  };

  const handleNext = () => {
    setCurrentPage(prevPage => prevPage + 1);
    onNext();
  };

  const handlePageSizeChange = (
    event: React.FormEvent<IComboBox>,
    option?: IComboBoxOption,
    index?: number,
    value?: string
  ) => {
    if (option) {
      // when the page size changes, we need to calculate what the page should be
      // the page to move must be the result of this formula
      // page = (current page * current page size + items) / new page size
      let newPage = Math.floor(
        (currentPage * pageSize + credentialsCollection.length) / Number(option.key)
      );

      setPageSize(Number(option.key));
      setCurrentPage(prevPage => Math.max(0, newPage)); // Reset to the first page
      // in the case the page is wider than the new page size, we need to call the service again with the current page but wider page size
      handleCurrentPageChange(Number(option.key));
    }
  };

  const comboStyles = { root: { width: 80 } };

  const pageSizeSelect = (
    <ComboBox
      styles={comboStyles}
      selectedKey={pageSize}
      onChange={handlePageSizeChange}
      options={[
        { key: 10, text: "10" },
        { key: 50, text: "50" },
        { key: 100, text: "100" },
      ]}
      ariaLabel="Select items page size"
      iconButtonProps={{ ariaLabel: "Select items page size" }}
    />
  );

  return (
    <>
      {headerContextMenu}
      <span style={{ marginBottom: 8, marginTop: 8, display: "block" }}>
        <b> Note:</b> You must create an ingestion source to ingest data into your
        collection.
      </span>
      <CredentialsCommandBar
        hasSelection={!!detailItem}
        onCreate={handleCommandBarButtonClick}
        onDelete={setShowDeleteDialog}
        onRefresh={handleRefresh}
      />

      {!isLoading && credentialsCollection.length === 0 ? (
        <NoRecords entryType={"credential"} handleCreateOpen={handleCreateOpen} />
      ) : (
        <Stack styles={containerStyles}>
          <Stack.Item styles={contentStyles}>{table}</Stack.Item>
          <Stack.Item styles={footerStyles}>
            <Stack horizontal>
              <DefaultButton
                onClick={handlePrevious}
                disabled={currentPage === 0}
                styles={buttonStyles}
                aria-label="Previous page"
              >
                &#60; Previous
              </DefaultButton>
              <DefaultButton
                onClick={handleNext}
                disabled={credentialsCollection.length < pageSize}
                styles={buttonStyles}
                aria-label="Next page"
              >
                Next &#62;
              </DefaultButton>
              {pageSizeSelect}
            </Stack>
          </Stack.Item>
        </Stack>
      )}
      {panelState.isOpen && (
        <CredentialPanel
          initialValue={detailItem}
          mode={panelMode}
          title="Create ingestion source"
          body={<PanelBodyCreate />}
          mutation={mutation}
          onDismiss={handlePanelClose}
          managedIdentities={managedIdentities}
          credentialTypes={credentialsTypes}
          resourceType="credential"
        />
      )}
      {showDeleteDialog && deleteDialog}
      {isError && errorDialog}
    </>
  );
};
